import { Box } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import SignInDialog from '../../components/common/SignInDialog';
import api from '../../redux/features/auth/axiosAuthHelper';
import SkuDetailCard from '../../components/common/cards/SkuDetailCard';
import RecordDetailDialog from '../../components/common/RecordDetailDialog';
import SkusTable from '../../components/common/tables/SkusTable';
import { useSkusTable } from '../useInventoryTables';

const SkusPage = () => {
  const authData = useSelector((state: RootState) => state.auth);
  const table = useSkusTable(authData.isAuthenticated, authData.access);
  const [sku, setSku] = useState<any>(null);
  const [popup, setPopup] = useState('');
  const [showGlobalFilter, setShowGlobalFilter] = useState(true);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
  const { paramID } = useParams<{ paramID: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const openedFromTable = useRef<string | null>(null);
  useEffect(() => {
    if (location.state?.fromTable) {
      openedFromTable.current = paramID ?? null;
      navigate(location.pathname, { replace: true, state: null });
      return;
    }
    if (paramID && openedFromTable.current === paramID) {
      openedFromTable.current = null;
      return;
    }
    openedFromTable.current = null;
    if (paramID?.toLowerCase() === 'create') {
      setPopup('Create');
      setSku(null);
    } else if (paramID && /^\d+$/.test(paramID)) {
      let active = true;
      api.get(`skus/${paramID}/`, {
        headers: { Authorization: `Bearer ${authData.access}` },
      }).then(response => {
        if (!active) return;
        setSku(response.data);
        setPopup('Edit');
      }).catch(error => {
        if (!active) return;
        setSku({ id: paramID, code: 'Not Found' });
        setPopup('Edit');
        console.error('Error fetching sku:', error);
      });
      return () => { active = false; };
    } else if (paramID) {
      navigate('/sku', { replace: true });
    }
  }, [paramID, location.pathname, location.state, navigate, authData.access]);

  const handleRefreshButton = () => {
    setSku(null);
    setPopup('');
    navigate('/sku', { replace: true });
    table.refresh();
  };

  const handleBackButton = () => {
    setSku(null);
    setPopup('');
    navigate('/sku', { replace: true });
  };

  const openEditPage = (row: any) => {
    setSku(row.original);
    setPopup('Edit');
    navigate(`/sku/${row.original.id}`, { replace: true, state: { fromTable: true } });
  };

  const openCreatePage = () => {
    setSku(null);
    setPopup('Create');
    navigate('/sku/create', { replace: true });
  };

  return authData.isAuthenticated === false ? (
    <SignInDialog isAuthenticated={authData.isAuthenticated} />
  ) : authData.isAuthenticated ? (
    <div data-testid="div-9bus" style={{ overflowX: "hidden", maxHeight: `100%`, minHeight: `100%`, maxWidth: `100%`, minWidth: `100%`, margin: "auto" }}>

      <Box sx={{ p: { xs: 1, md: 1.5 }, height: '100dvh', minHeight: 360, boxSizing: 'border-box', minWidth: 0 }}>
                    <SkusTable
                      rows={table.rows}
                      rowCount={table.rowCount}
                      isLoading={table.isLoading}
                      hasLoaded={table.hasLoaded}
                      error={table.error}
                      refresh={table.refresh}
                      sorting={table.sorting}
                      columnVisibility={columnVisibility}
                      columnFilters={table.columnFilters}
                      pagination={table.pagination}
                      globalFilter={table.globalFilter}
                      rowSelection={table.rowSelection}
                      setRowSelection={table.setRowSelection}
                      showGlobalFilter={showGlobalFilter}
                      setShowGlobalFilter={setShowGlobalFilter}
                      handleRemoveAllFilters={table.clearFilters}
                      setGlobalFilter={table.onGlobalFilterChange}
                      setSorting={table.onSortingChange}
                      setColumnVisibility={setColumnVisibility}
                      setColumnFilters={table.onColumnFiltersChange}
                      setPagination={table.onPaginationChange}
                      handleRefreshButton={handleRefreshButton}
                      openEditPage={openEditPage}
                      openCreatePage={openCreatePage}
                    />
      </Box>
      <RecordDetailDialog open={(!!sku && popup === 'Edit') || popup === 'Create'} title={popup === 'Create' ? 'Create SKU' : 'Edit SKU'} onClose={handleBackButton} wide compactHeader>
        {popup === 'Create' ? (
          <SkuDetailCard handleRefreshButton={handleRefreshButton} handleBackButton={handleBackButton} />
        ) : sku ? (
          <SkuDetailCard sku_id={sku.id} handleRefreshButton={handleRefreshButton} handleBackButton={handleBackButton} onStatusChanged={table.refresh} />
        ) : null}
      </RecordDetailDialog>
    </div>
  ) : null;
};

export default SkusPage;
